import { SupportChatProps } from '@acme/domains';
import { createEffect, createEvent, createStore, sample } from 'effector';
import {
  $lastMessage,
  MessageHubMessage,
  getChatIdFromMessage,
} from '../../../shared/messageHub/model';
import { ChatState, ChatWindowApi, Message, initialChatState } from './types';

const mergeMessagesUnique = (incoming: Message[], existing: Message[]) => {
  const existingIds = new Set<string>();
  for (const msg of existing) {
    if (msg.id) existingIds.add(msg.id);
  }

  const result: Message[] = [];
  for (const msg of incoming) {
    if (!msg.id) {
      result.push(msg);
      continue;
    }
    if (!existingIds.has(msg.id)) {
      existingIds.add(msg.id);
      result.push(msg);
    }
  }

  return [...result, ...existing];
};

const isSystemMessage = (response: Message): boolean => {
  return (
    response.text?.startsWith('@@') ||
    (response.text?.includes('connected') &&
      response.text.includes('disconnected')) ||
    false
  );
};

// Factory для создания модели чата с привязкой к chatId
export const createChatModel = (chatId: string) => {
  // Events
  const setApi = createEvent<ChatWindowApi>();
  const loadMessages = createEvent();
  const loadMoreMessages = createEvent();
  const addMessage = createEvent<Message>();
  const setMessages = createEvent<Message[]>();
  const resetChat = createEvent();
  const setPage = createEvent<number>();
  const markNewMessagesSeen = createEvent();
  const sendMessage = createEvent<SupportChatProps['sendMessage'] extends (
    req: infer Req
  ) => any
    ? Req
    : never>();

  // Effects
  const loadMessagesFx = createEffect(
    async ({
      loader,
      page,
      pageSize,
    }: {
      loader: SupportChatProps['loadChatHistory'];
      page: number;
      pageSize: number;
    }) => {
      const response = await loader({ pageNumber: page, pageSize });

      if (!response.isSuccess || !response.data || !response.data.items) {
        throw new Error(response.errorText || 'Load chat history error');
      }

      const messageList = response.data.items || [];

      const sortedList = messageList.sort(
        (a, b) =>
          new Date((a.sentAt || a.readAt) ?? '').getTime() -
          new Date((b.sentAt || b.readAt) ?? '').getTime()
      );

      return {
        messages: sortedList as Message[],
        totalPages: response.data.totalPages ?? null,
        totalCount: response.data.totalCount ?? null,
        page,
      };
    }
  );

  const sendMessageFx = createEffect(
    async ({
      sender,
      req,
    }: {
      sender: SupportChatProps['sendMessage'];
      req: SupportChatProps['sendMessage'] extends (r: infer R) => any ? R : never;
    }) => {
      return await sender(req);
    }
  );

  const $api = createStore<ChatWindowApi | null>(null).on(setApi, (_, api) => api);
  const hasApi = (source: { api: ChatWindowApi | null }): source is {
    api: ChatWindowApi;
  } => source.api !== null;

  // Store
  const $chat = createStore<ChatState>(initialChatState)
    .on(loadMessages, (state) => ({
      ...state,
      isLoading: true,
      isLoadingMore: false,
      currentPage: 1,
      hasMoreMessages: true,
      totalPages: null,
      totalCount: null,
      error: null,
    }))
    .on(loadMoreMessages, (state) => ({
      ...state,
      isLoadingMore: true,
      error: null,
    }))
    .on(loadMessagesFx.doneData, (state, { messages, totalPages, totalCount, page }) => ({
      ...state,
      messages:
        page === 1 ? messages : mergeMessagesUnique(messages, state.messages),
      totalPages,
      totalCount,
      hasMoreMessages: totalPages === null ? state.hasMoreMessages : page < totalPages,
      isLoading: false,
      isLoadingMore: false,
      error: null,
    }))
    .on(loadMessagesFx.failData, (state, error) => ({
      ...state,
      isLoading: false,
      isLoadingMore: false,
      error: error.message,
    }))
    .on(sendMessageFx.failData, (state, error) => ({
      ...state,
      error: error.message,
    }))
    .on(addMessage, (state, message) => {
      if (isSystemMessage(message)) {
        return state;
      }
      return {
        ...state,
        messages: [...state.messages, message],
        hasNewMessages: true,
      };
    })
    .on(setMessages, (state, messages) => ({
      ...state,
      messages,
    }))
    .on(setPage, (state, page) => ({
      ...state,
      currentPage: page,
    }))
    .on(markNewMessagesSeen, (state) => ({
      ...state,
      hasNewMessages: false,
    }))
    .reset(resetChat);

  // Derived stores
  const $messages = $chat.map((state) => state.messages);
  const $isLoading = $chat.map((state) => state.isLoading);
  const $isLoadingMore = $chat.map((state) => state.isLoadingMore);
  const $hasMoreMessages = $chat.map((state) => state.hasMoreMessages);
  const $currentPage = $chat.map((state) => state.currentPage);
  const $hasNewMessages = $chat.map((state) => state.hasNewMessages);
  const $error = $chat.map((state) => state.error);
  const $companionLabelText = $api.map(
    (api) => api?.companionLabelText ?? 'Собеседник'
  );
  const $getAttachmentReq = $api.map((api) => api?.getAttachmentReq ?? null);

  // Sample для загрузки сообщений
  sample({
    clock: loadMessages,
    source: { state: $chat, api: $api },
    filter: hasApi,
    fn: ({ state, api }) => ({
      loader: api!.loadChatHistory,
      page: 1,
      pageSize: 10,
    }),
    target: loadMessagesFx,
  });

  sample({
    clock: loadMoreMessages,
    source: { state: $chat, api: $api },
    filter: ({ state, api }) =>
      api !== null &&
      state.hasMoreMessages &&
      (state.totalPages === null || state.currentPage < state.totalPages),
    fn: ({ state, api }) => ({
      loader: api!.loadChatHistory,
      page: state.currentPage + 1,
      pageSize: 10,
    }),
    target: loadMessagesFx,
  });

  sample({
    clock: sendMessage,
    source: $api,
    filter: (api): api is ChatWindowApi => api !== null,
    fn: (api, req) => ({
      sender: api!.sendMessage,
      req,
    }),
    target: sendMessageFx,
  });

  // Обновляем page после успешной загрузки
  sample({
    clock: loadMessagesFx.doneData,
    fn: ({ page }) => page,
    target: setPage,
  });

  // Фильтрация входящих сообщений по chatId
  sample({
    clock: $lastMessage,
    filter: (message): message is MessageHubMessage =>
      message !== null && getChatIdFromMessage(message) === chatId,
    fn: (message) => message as Message,
    target: addMessage,
  });

  return {
    // Events
    setApi,
    loadMessages,
    loadMoreMessages,
    addMessage,
    setMessages,
    resetChat,
    setPage,
    markNewMessagesSeen,
    sendMessage,

    // Stores
    $api,
    $chat,
    $messages,
    $isLoading,
    $isLoadingMore,
    $hasMoreMessages,
    $currentPage,
    $hasNewMessages,
    $error,
    $companionLabelText,
    $getAttachmentReq,

    // Effects
    loadMessagesFx,
    sendMessageFx,
  };
};

export type ChatModel = ReturnType<typeof createChatModel>;
